import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/api";
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiLock, FiEye, FiEyeOff, FiCalendar, FiX, FiUsers, FiBarChart, FiDollarSign, FiClock, FiCheckCircle, FiSmartphone, FiShield, FiTrendingUp, FiPackage, FiMessageSquare, FiDatabase, FiActivity } from "react-icons/fi";
import { IoSunnyOutline, IoMoonOutline } from "react-icons/io5";

const LoginModal = ({ isOpen, onClose }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/api/login/", {
        username,
        password,
      });
      if (response.data && response.data.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("permissions", JSON.stringify(response.data.user.permissions));

        try {
          const companyResponse = await axiosInstance.get("/api/company/");
          if (companyResponse.data && companyResponse.data.length > 0) {
            const companyName = companyResponse.data[0].name;
            localStorage.setItem("companyName", companyName);
          }
        } catch (companyError) {
          console.error("Şirket bilgisi alınamadı:", companyError);
        }

        navigate("/");
      } else {
        throw new Error("Token alınamadı");
      }
    } catch (error) {
      console.error("Giriş başarısız:", error);
      setError(
        "Giriş başarısız. Lütfen bilgilerinizi kontrol edip tekrar deneyin."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md relative overflow-hidden"
          >
            <div className="h-32 relative overflow-hidden">
              <img
                src="https://i.hizliresim.com/q823881."
                alt="Login Banner"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60"></div>
              <h2 className="absolute bottom-4 left-6 text-2xl font-semibold text-white">
                Hoş Geldiniz
              </h2>
            </div>
            <div className="p-6">
            <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 rounded-full bg-gray-100/80 dark:bg-gray-700/50 hover:bg-gray-200/90 dark:hover:bg-gray-600/70 transition-colors duration-200 group backdrop-blur-sm"
              >
                <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
              </button>

              <h2 className="text-2xl font-semibold text-blue-600 dark:text-blue-400 mb-6">
                Giriş Yap
              </h2>

              {error && (
                <p className="text-red-600 dark:text-red-400 text-sm mb-4 bg-red-100 dark:bg-red-900 p-3 rounded-lg">
                  {error}
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kullanıcı Adı
                  </label>
                  <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-300"
                      placeholder="Kullanıcı Adınız"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Şifre
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-12 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 transition duration-300"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <a
                    href="https://admin.sbsflow.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition duration-300"
                  >
                    Şifremi Unuttum
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-2 px-4 rounded-lg text-white font-medium ${isLoading
                      ? "bg-blue-400 dark:bg-blue-600 cursor-not-allowed"
                      : "bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
                    } transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Giriş Yapılıyor...
                    </span>
                  ) : (
                    "Giriş Yap"
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Login = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);
    updateTheme(isDarkMode);
  }, []);

  const updateTheme = (isDark) => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem("darkMode", newDarkMode.toString());
    updateTheme(newDarkMode);
  };

  const additionalFeatures = [
    {
      icon: <FiSmartphone className="text-blue-600 dark:text-blue-400" />,
      title: 'Mobil Uyumlu',
      description: 'Her cihazda mükemmel çalışan responsive tasarım'
    },
    {
      icon: <FiShield className="text-blue-600 dark:text-blue-400" />,
      title: 'Güvenli Altyapı',
      description: 'SSL sertifikalı ve şifrelenmiş veri transferi'
    },
    {
      icon: <FiTrendingUp className="text-blue-600 dark:text-blue-400" />,
      title: 'Sürekli Destek',
      description: '7/24 teknik destek ve yardım'
    },
    {
      icon: <FiActivity className="text-blue-600 dark:text-blue-400" />,
      title: 'Kolay Kullanım',
      description: 'Kullanıcı dostu arayüz ve hızlı işlemler'
    }
  ];

  const features = [
    {
      icon: <FiCalendar className="w-8 h-8" />,
      title: 'Randevu Yönetimi',
      description: 'Müşterilerinizin randevularını kolayca yönetin. SMS hatırlatmaları ve çakışma kontrolü ile randevu takibini otomatikleştirin.',
      rem: '2rem',
      gradient: 'from-blue-500 to-blue-400',
      subFeatures: ['SMS Hatırlatma', 'Randevu Takvimi', 'Çakışma Kontrolü']
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: 'Müşteri Takibi',
      description: 'Detaylı müşteri kartları ve geçmiş işlemler. Otomatik bildirimler ile müşterilerinizle iletişimi güçlendirin.',
      rem: '2.5rem',
      gradient: 'from-blue-600 to-blue-500',
      subFeatures: ['Müşteri Kartı', 'İşlem Geçmişi', 'Otomatik Bildirim']
    },
    {
      icon: <FiDollarSign className="w-8 h-8" />,
      title: 'Ödeme ve Taksit',
      description: 'Gelişmiş ödeme takibi ve taksit planları. Borç/alacak takibi ve vade hatırlatmaları ile nakit akışınızı kontrol edin.',
      rem: '2rem',
      gradient: 'from-blue-500 to-blue-600',
      subFeatures: ['Taksit Planı', 'Vade Takibi', 'Kasa Raporu']
    },
    {
      icon: <FiBarChart className="w-8 h-8" />,
      title: 'İş Analizi',
      description: 'Gerçek zamanlı istatistikler ve raporlar. Performans takibi ve trend analizleri ile işletmenizi daha iyi yönetin.',
      rem: '3rem',
      gradient: 'from-blue-400 to-blue-500',
      subFeatures: ['Ciro Takibi', 'Performans Analizi', 'Dönemsel Raporlar']
    },
    {
      icon: <FiPackage className="w-8 h-8" />,
      title: 'Ürün Yönetimi',
      description: 'Stok takibi ve ürün kategorileri. Minimum stok uyarıları ve barkod sistemi ile envanterinizi kontrol altında tutun.',
      rem: '2rem',
      gradient: 'from-blue-500 to-blue-400',
      subFeatures: ['Stok Takibi', 'Barkod Sistemi', 'Ürün Kartı']
    },
    {
      icon: <FiMessageSquare className="w-8 h-8" />,
      title: 'Müşteri İletişimi',
      description: 'Toplu SMS gönderimi ve kampanya duyuruları. Otomatik bildirimler ile müşteri ilişkilerinizi güçlendirin.',
      rem: '2rem',
      gradient: 'from-blue-500 to-blue-600',
      subFeatures: ['Toplu SMS', 'Kampanya Bildirimi', 'Özel Gün Mesajları']
    },
    {
      icon: <FiDatabase className="w-8 h-8" />,
      title: 'Çoklu Şirket',
      description: 'Birden fazla şubenizi veya işletmenizi tek bir platformdan yönetin. Merkezi kontrol ve raporlama sistemi.',
      rem: '2.5rem',
      gradient: 'from-blue-600 to-blue-500',
      subFeatures: ['Şube Yönetimi', 'Merkezi Kontrol', 'Konsolide Raporlar']
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: 'Sektörel Çözümler',
      description: 'Farklı sektörlere özel randevu ve müşteri yönetim çözümleri. Her sektörün ihtiyacına uygun özelleştirilebilir sistem.',
      rem: '2rem',
      gradient: 'from-blue-500 to-blue-400',
      subFeatures: ['Sağlık Sektörü', 'Güzellik Merkezleri', 'Danışmanlık']
    }
  ];

  const stats = [
    { value: '98+', label: 'Aktif Kullanıcı', icon: <FiUsers /> },
    { value: '48.000+', label: 'Randevu', icon: <FiCalendar /> },
    { value: '24/7', label: 'Destek', icon: <FiClock /> },
    { value: '47+', label: 'İşletme', icon: <FiDatabase /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white dark:from-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-8">
              <h1 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                SBS Flow
              </h1>
              <nav className="hidden md:flex space-x-8">
                <a
                  href="#features"
                  className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#features').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Özellikler
                </a>
                <a
                  href="#contact"
                  className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  İletişim
                </a>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
              >
                {darkMode ? <IoSunnyOutline size={20} /> : <IoMoonOutline size={20} />}
              </button>
              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-blue-600 dark:bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-300"
              >
                Giriş Yap
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center relative">
          <div className="md:w-1/2 mb-12 md:mb-0">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight"
            >
              İşletmenizi
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">
                {" "}SBS Flow{" "}
              </span>
              ile Yönetin
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed"
            >
              Randevu yönetimi, müşteri takibi ve ödeme sistemi tek bir platformda.
              İşletmenizi daha verimli yönetmek için tasarlandı.
              <span className="block mt-4 text-blue-600 dark:text-blue-400 font-semibold">
                Hemen başlayın ve işletmenizi büyütün!
              </span>
            </motion.p>
          </div>
          <div className="md:w-1/2 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-3xl filter blur-3xl"></div>
            <div className="relative bg-white/50 dark:bg-gray-800/50 rounded-3xl p-8 backdrop-blur-sm shadow-xl">
              <div className="grid grid-cols-2 gap-6">
                {additionalFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex flex-col items-center text-center p-4"
                  >
                    <div className="text-blue-600 dark:text-blue-400 text-2xl mb-3">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Randevu şeysi */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 dark:from-blue-800 dark:via-blue-900 dark:to-blue-950 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url("https://i.hizliresim.com/l3dqc08.")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 transform hover:scale-105 transition-transform duration-300"
              >
                <div className="text-white/80 text-3xl mb-4 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-blue-100">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Özellikler*/}
      <section id="features" className="py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url("https://i.hizliresim.com/ahd3zur.")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Özellikler
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-300"
            >
              İşletmenizi büyütmek için ihtiyacınız olan her şey
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden"
                style={{ marginTop: feature.rem }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-bl-[100px] transform rotate-12 group-hover:scale-150 transition-transform duration-500"></div>

                <div className="relative z-10">
                  <div className={`text-3xl mb-6 w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-r ${feature.gradient} text-white transform group-hover:rotate-6 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300 mb-6">
                    {feature.description}
                  </p>

                  <div className="space-y-3">
                    {feature.subFeatures.map((subFeature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.gradient}`}></div>
                        <span className="text-gray-700 dark:text-gray-300">
                          {subFeature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektörler */}
      <section className="py-20 bg-gradient-to-br from-white to-blue-50 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url("https://i.hizliresim.com/q823881.")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-6"
            >
              Kimler Kullanabilir?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
            >
              SBS Flow, farklı sektörlerdeki işletmelere özel çözümler sunar.
              Her sektörün kendine özgü ihtiyaçlarına uygun özelleştirilebilir yapı.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-bl-full transform rotate-12 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-blue-600 dark:text-blue-400 text-3xl mb-6">
                <FiUser className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Sağlık Profesyonelleri
              </h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300 relative z-10">
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-blue-500" />
                  <span>Psikologlar ve Terapistler</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-blue-500" />
                  <span>Diş Hekimleri</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-blue-500" />
                  <span>Fizyoterapistler</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-blue-500" />
                  <span>Veteriner Klinikleri</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Randevu planlaması, hasta takibi ve özel seans yönetimi
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-bl-full transform rotate-12 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-purple-600 dark:text-purple-400 text-3xl mb-6">
                <FiUsers className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
                Güzellik ve Bakım
              </h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300 relative z-10">
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-purple-500" />
                  <span>Güzellik Merkezleri</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-purple-500" />
                  <span>Kuaförler ve Berberler</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-purple-500" />
                  <span>Masaj ve Spa Salonları</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-purple-500" />
                  <span>Cilt Bakım Merkezleri</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Hizmet planlaması, müşteri takibi ve kampanya yönetimi
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-bl-full transform rotate-12 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-green-600 dark:text-green-400 text-3xl mb-6">
                <FiMessageSquare className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-green-600 to-teal-500 bg-clip-text text-transparent">
                Danışmanlık Hizmetleri
              </h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300 relative z-10">
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-green-500" />
                  <span>Avukatlar</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-green-500" />
                  <span>Finansal Danışmanlar</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-green-500" />
                  <span>Yaşam Koçları</span>
                </li>
                <li className="flex items-center space-x-2">
                  <FiCheckCircle className="text-green-500" />
                  <span>Spor Eğitmenleri</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Danışan yönetimi, seans planlaması ve takip sistemi
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Arama kısımcığı */}
      <section id="contact" className="py-16 bg-gradient-to-br from-gray-50 via-blue-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url("https://i.hizliresim.com/qxv4p9u.")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-6"
            >
              İletişime Geçin
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-300"
            >
              Sorularınız için bize ulaşın
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-2xl mx-auto transform hover:scale-105 transition-all duration-300"
          >
            <div className="flex items-center justify-center space-x-8 text-lg text-gray-600 dark:text-gray-300 mb-8">
              <div className="flex items-center space-x-3 bg-blue-50 dark:bg-blue-900/30 px-4 py-2 rounded-xl">
                <FiClock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <span>7/24 Destek</span>
              </div>
              <div className="flex items-center space-x-3 bg-blue-50 dark:bg-blue-900/30 px-4 py-2 rounded-xl">
                <FiCheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <span>Hızlı Çözüm</span>
              </div>
            </div>
            <div className="text-center space-y-4">
              <p className="text-3xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">
                +90 539 439 34 44
              </p>
              <p className="text-gray-600 dark:text-gray-300 text-lg">
               cloudvdestek@gmail.com
              </p>
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Teknik destek ve satış için 7/24 hizmetinizdeyiz
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
};

export default Login;